/**
 * Module dependencies.
 */

import { Button, Checkbox, HelpText, Input, Select } from 'src/components/core';
import { Filter, useAdvisor } from 'src/context/advisor/context';
import { FormDataProps } from 'src/types/forms';
import { ReactElement, useCallback } from 'react';
import { advisorFiltersKeys, advisorFormKeys } from 'src/core/advisor-config';
import { media, units } from '@untile/react-components';
import { regexes } from 'src/core/utils/regexes';
import { submitAdvisorForm } from 'src/api/form/submit-advisor-form';
import { useCountriesList } from 'src/hooks/use-countries-list';
import { useForm } from 'react-hook-form';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useLocalizedRoute } from 'src/core/utils/routes';
import { useMutation } from 'react-query';
import { useRouter } from 'next/router';
import { useSnackbar } from 'src/context/snackbar/context';
import TermsConditionsCheckboxLabel from 'src/components/core/forms/terms-conditions-checkbox-label';
import map from 'lodash/map';
import omit from 'lodash/omit';
import styled from 'styled-components';
import useTranslate from 'src/hooks/use-translate';

/**
 * `Props` type.
 */

type Props = {
  className?: string;
};

/**
 * `StyledCheckbox` styled component.
 */

const StyledCheckbox = styled(Checkbox)`
  margin-bottom: ${units(5)};
`;

/**
 * `StyledButton` styled component.
 */

const StyledButton = styled(Button)`
  ${media.max('sm')`
    width: 100%;
  `}
`;

/**
 * `normalizeFilters`.
 */

function normalizeFilters(filters: Filter[]) {
  const normalizedKeys = {};

  map(filters, filter => {
    const filterKey = Object.keys(filter)[0];
    const value = filter[filterKey];

    if (
      filterKey in
        [
          advisorFiltersKeys.butane,
          advisorFiltersKeys.propane,
          advisorFiltersKeys.waterCapacity
        ] &&
      typeof value === 'object'
    ) {
      const { from, to } = value;

      normalizedKeys[advisorFormKeys[filterKey]] = Math.floor(
        (Number(from) + Number(to)) / 2
      );

      return;
    }

    normalizedKeys[advisorFormKeys[filterKey]] = filter[filterKey];
  });

  return normalizedKeys;
}

/**
 * `ContactForm` component.
 */

const ContactForm = ({ className }: Props): ReactElement => {
  const router = useRouter();
  const routeResolver = useLocalizedRoute();
  const { locale, translate } = useTranslate();
  const { showMessage } = useSnackbar();
  const { filters } = useAdvisor();
  const {
    formState: { errors },
    handleSubmit,
    register,
    reset
  } = useForm({
    mode: 'onBlur'
  });

  const { isLoading: isSubmitting, mutateAsync } =
    useMutation(submitAdvisorForm);

  const countriesList = useCountriesList();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const onSubmit = useCallback(
    async (data: FormDataProps) => {
      const normalizedFilters = normalizeFilters(filters);
      const recaptchaToken = await executeRecaptcha('advisorForm').then(
        token => token
      );

      const formData = {
        ...omit(data, 'acceptTerms'),
        ...normalizedFilters,
        // eslint-disable-next-line id-match
        recaptcha_token: recaptchaToken
      };

      return mutateAsync(
        { formData, locale },
        {
          onError: () => {
            showMessage(translate('advisor:steps.contactForm.messages.error'), {
              appearance: 'error'
            });
          },
          onSuccess: () => {
            setTimeout(() => {
              router.push(routeResolver('home'));
            }, 4000);

            reset();
            showMessage(
              translate('advisor:steps.contactForm.messages.success'),
              {
                appearance: 'success'
              }
            );
          }
        }
      );
    },
    [
      executeRecaptcha,
      filters,
      locale,
      mutateAsync,
      reset,
      routeResolver,
      router,
      showMessage,
      translate
    ]
  );

  return (
    <form
      className={className}
      onSubmit={handleSubmit(onSubmit)}
    >
      <Input
        disabled={isSubmitting}
        error={errors?.company}
        isRequired
        label={translate('forms:labels.company')}
        name={'company'}
        placeholder={translate('forms:placeholders.company')}
        {...register('company', {
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      />

      <Input
        disabled={isSubmitting}
        error={errors?.name}
        isRequired
        label={translate('forms:labels.name')}
        name={'name'}
        placeholder={translate('forms:placeholders.name')}
        {...register('name', {
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      />

      <Input
        disabled={isSubmitting}
        error={errors?.position}
        label={translate('forms:labels.position')}
        name={'position'}
        placeholder={translate('forms:placeholders.position')}
        {...register('position')}
      />

      <Input
        disabled={isSubmitting}
        error={errors?.email}
        isRequired
        label={translate('forms:labels.email')}
        name={'email'}
        placeholder={translate('forms:placeholders.email')}
        {...register('email', {
          pattern: {
            message: translate('forms:rules.email'),
            value: regexes.email
          },
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      />

      <Input
        disabled={isSubmitting}
        error={errors?.phone}
        label={translate('forms:labels.phone')}
        name={'phone'}
        placeholder={translate('forms:placeholders.phone')}
        {...register('phone')}
      />

      <Select
        disabled={isSubmitting}
        error={errors?.country}
        isRequired
        label={translate('forms:labels.country')}
        name={'country'}
        {...register('country', {
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      >
        <>
          <option value={''}>{translate('forms:placeholders.select')}</option>

          {map(countriesList, ({ alpha2, name }) => (
            <option
              key={alpha2}
              value={name}
            >
              {name}
            </option>
          ))}
        </>
      </Select>

      <Input
        as={'textarea'}
        disabled={isSubmitting}
        error={errors?.message}
        label={translate('forms:labels.message')}
        name={'message'}
        placeholder={translate('forms:placeholders.message')}
        {...register('message')}
      />

      <Checkbox
        disabled={isSubmitting}
        label={
          <TermsConditionsCheckboxLabel
            i18nKey={'forms:labels.termsConditions'}
          />
        }
        name={'acceptTerms'}
        {...register('acceptTerms', {
          required: {
            message: translate('forms:rules.required'),
            value: true
          }
        })}
      />

      {errors.acceptTerms && (
        <HelpText hasError>
          {translate('forms:rules.acceptTermsConditions')}
        </HelpText>
      )}

      <StyledCheckbox
        disabled={isSubmitting}
        id={'subscribeToNewsletter'}
        label={translate('forms:labels.allowMarketing')}
        name={'subscribeToNewsletter'}
        {...register('subscribeToNewsletter')}
      />

      <StyledButton
        isLoading={isSubmitting}
        size={'medium'}
        type={'submit'}
      >
        {translate('common:actions.submit')}
      </StyledButton>
    </form>
  );
};

/**
 * Export `ContactForm` component.
 */

export default ContactForm;
